import { BootAuthResponse } from '@/lib/types';
import { AuthMethods } from '@/lib/consts/auth';
import { LoggerEventNames } from '@/lib/consts/logger';
import { FeatureFlags } from '@/lib/consts/featureFlags';
import { PlayerData } from '@/lib/types/player';
import { getAnalyticsEnvironment } from '@/lib/utils/analytics';
import { getEnvVariable } from '@/lib/utils/env';
import type { HandoffTokenIdentity } from '@/lib/utils/handoffTokenIdentity';
import {
    clearPendingRumIdentity,
    getPendingRumIdentity,
    setPendingRumIdentity,
} from '@/lib/analytics/pendingRumIdentity';
import {
    RumEventLevel,
    carryRumEventToNextDocument,
    drainCarriedRumEvents,
} from '@/lib/analytics/pendingRumEvents';
import { getStoreJourneyContext } from '@/lib/analytics/storeJourney';

type CoralogixSdk = typeof import('@web-store-platforms/analytics/src/coralogix');
type CoralogixOperation = (sdk: CoralogixSdk) => void;

const MAX_QUEUED_OPERATIONS = 100;
const DEFAULT_SESSION_SAMPLE_RATE = 30;
const FULL_SESSION_SAMPLE_RATE = 100;
const JOURNEY_EVENT_NAMES = new Set<LoggerEventNames>([
    LoggerEventNames.LOGIN_LANDED,
    LoggerEventNames.LOGIN_ATTEMPTED,
    LoggerEventNames.LOGIN_COMPLETED,
    LoggerEventNames.LOGIN_FAILED,
    LoggerEventNames.LOGIN_ERROR,
    LoggerEventNames.LOGIN_DEAD_END,
    LoggerEventNames.FAILED_PAGE_DISPLAYED,
    LoggerEventNames.SHOP_LANDED,
]);

/** Separates an identity proven by a login from one merely read off a token. */
enum IdentitySource {
    LOGIN = 'login',
    HANDOFF_TOKEN = 'handoff-token',
}

interface PlayerIdentityLabels {
    playerId: string;
    playerName: string;
    playerCountry: string;
    authMethod: string;
    identitySource: IdentitySource;
}

/**
 * A login is proof; a token is a claim. Ranking them means a completed login can
 * never be overwritten by the handoff identity the same journey started with,
 * whatever order the two arrive in.
 */
const IDENTITY_SOURCE_RANK: Record<IdentitySource, number> = {
    [IdentitySource.HANDOFF_TOKEN]: 1,
    [IdentitySource.LOGIN]: 2,
};

export class CoralogixRumService {
    private isInitialized = false;
    private sdk: CoralogixSdk | undefined;
    private initializationPromise: Promise<void> | undefined;
    private publisherMetadata: Record<string, string> = {};
    private readonly operationQueue: CoralogixOperation[] = [];
    // Held as state rather than queued with the events: an event carries whatever
    // identity is attached the instant it is emitted, so the identity has to be
    // resolvable before any event is released.
    private playerIdentity: PlayerIdentityLabels | undefined;

    sendErrorEvent(eventName: LoggerEventNames, eventData: Record<string, unknown>): void {
        this.runOrQueue((sdk) =>
            sdk.logCoralogixError(
                eventName,
                enrichStoreJourneyEvent(eventName, eventData)
            )
        );
    }

    sendInfoEvent(eventName: LoggerEventNames, eventData: Record<string, unknown>): void {
        this.runOrQueue((sdk) =>
            sdk.logCoralogixInfo(
                eventName,
                enrichStoreJourneyEvent(eventName, eventData)
            )
        );
    }

    /**
     * For an event raised immediately before a hard navigation. Reporting it here
     * would lose it: on /login RUM is still initializing behind the first
     * content paint, and even
     * once initialized the SDK batches over the network without flushing on
     * unload. The next document reports it instead.
     */
    sendEventAcrossNavigation(
        level: RumEventLevel,
        eventName: LoggerEventNames,
        eventData: Record<string, unknown>
    ): void {
        carryRumEventToNextDocument({
            level,
            name: eventName,
            data: enrichStoreJourneyEvent(eventName, eventData),
        });
    }

    setPlayerContext(playerData: PlayerData): void {
        this.applyPlayerIdentity({
            playerId: playerData.playerId,
            playerName: playerData.playerName ?? '',
            playerCountry: playerData.playerCountry ?? '',
            authMethod: playerData.authMethod ?? '',
            identitySource: IdentitySource.LOGIN,
        });
    }

    /**
     * A pre-auth landing already knows the player: the portal signs the id into
     * the handoff token. Attaching it before the exchange is the only way a
     * landing that never completes login stays attributable — the case
     * publishers report as "the player vanished".
     */
    setHandoffPlayerContext(identity: HandoffTokenIdentity): void {
        setPendingRumIdentity(identity);
        this.applyPlayerIdentity({
            playerId: identity.playerId,
            playerName: identity.playerName ?? '',
            playerCountry: identity.playerCountry ?? '',
            authMethod: AuthMethods.PORTAL_REDIRECT,
            identitySource: IdentitySource.HANDOFF_TOKEN,
        });
    }

    resetUserContext(): void {
        clearPendingRumIdentity();
        this.playerIdentity = undefined;
        this.runOrQueue((sdk) => {
            sdk.setCoralogixUserContext({
                userId: '',
                userName: '',
                email: '',
                metadata: this.publisherMetadata,
            });
            sdk.setCoralogixLabels({
                playerId: '',
                playerName: '',
                playerCountry: '',
                authMethod: '',
                identitySource: '',
                isTempSession: 'false',
            });
        });
    }

    initialize(authBoot: BootAuthResponse): void {
        if (this.isInitialized || this.initializationPromise || typeof window === 'undefined') {
            return;
        }

        this.publisherMetadata = {
            publisherId: authBoot.storeTheme.publisherId,
            environment: getAnalyticsEnvironment(),
        };
        this.initializationPromise = import('@web-store-platforms/analytics/src/coralogix')
            .then((sdk) => {
                sdk.initCoralogix({
                    publicKey: getEnvVariable('NEXT_PUBLIC_CORALOGIX_PUBLIC_KEY') ?? '',
                    application: 'appchrage-store-2.0',
                    version: getEnvVariable('NEXT_PUBLIC_STORE_VERSION') ?? '1.0.0',
                    domain: 'US2',
                    sessionSampleRate: this.resolveSessionSampleRate(authBoot),
                });
                this.sdk = sdk;
                // NOTE: Do NOT use the session/auth token as `userId` — it would leak
                // the bearer credential into the RUM payload. The real `playerId`
                // comes from the login hook or the restore below.
                sdk.setCoralogixUserContext({
                    userId: '',
                    metadata: this.publisherMetadata,
                });
                // Resolve who this is before releasing anything. Events raised
                // before init are queued, and the landing plus the failure that
                // followed it are usually both in that queue — flushing them
                // first would ship exactly those events with no player attached.
                return this.restorePlayerContext().finally(() => {
                    this.isInitialized = true;
                    if (this.playerIdentity) {
                        this.writePlayerIdentity(sdk, this.playerIdentity);
                    }
                    // Events handed over by the previous document came first in
                    // time, and by now carry the identity restored above.
                    this.emitCarriedEvents(sdk);
                    this.flushOperationQueue();
                });
            })
            .catch((error) => {
                console.error('Failed to initialize Coralogix RUM', error);
            })
            .finally(() => {
                this.initializationPromise = undefined;
            });
    }

    // Successful logins hard-navigate to /shop, which tears down the JS context
    // and with it the identity attached at login. Re-attach it from the persisted
    // player so the shop session, refreshes and extra tabs stay searchable.
    // Imported lazily: webStorageUtil reports its own errors through this service.
    private async restorePlayerContext(): Promise<void> {
        try {
            const { default: playerService } = await import('./player');
            const playerData = playerService.getPlayerData();
            if (playerData?.playerId) {
                this.setPlayerContext(playerData);
                return;
            }
            // No logged-in player, but a handoff landing in this tab may have
            // read one off the token before failing. /failed is the page that
            // most needs it, and it is reached by a hard navigation.
            const pendingIdentity = getPendingRumIdentity();
            if (pendingIdentity) {
                this.setHandoffPlayerContext(pendingIdentity);
            }
        } catch (error) {
            console.error('Failed to restore Coralogix RUM player context', error);
        }
    }

    private emitCarriedEvents(sdk: CoralogixSdk): void {
        drainCarriedRumEvents().forEach(({ level, name, data }) => {
            const eventData = enrichStoreJourneyEvent(name, data);
            if (level === RumEventLevel.INFO) {
                sdk.logCoralogixInfo(name, eventData);
                return;
            }
            sdk.logCoralogixError(name, eventData);
        });
    }

    private applyPlayerIdentity(identity: PlayerIdentityLabels): void {
        if (!this.outranksCurrentIdentity(identity)) {
            return;
        }
        this.playerIdentity = identity;
        if (this.isInitialized && this.sdk) {
            this.writePlayerIdentity(this.sdk, identity);
        }
    }

    // Equal rank means a later identity of the same kind wins, so a second
    // handoff or a different player logging in still takes over.
    private outranksCurrentIdentity(identity: PlayerIdentityLabels): boolean {
        const current = this.playerIdentity;
        return (
            !current ||
            IDENTITY_SOURCE_RANK[identity.identitySource] >=
                IDENTITY_SOURCE_RANK[current.identitySource]
        );
    }

    private writePlayerIdentity(sdk: CoralogixSdk, identity: PlayerIdentityLabels): void {
        sdk.setCoralogixUserContext({
            userId: identity.playerId,
            userName: identity.playerName,
            metadata: this.publisherMetadata,
        });
        sdk.setCoralogixLabels({ ...identity });
    }

    private resolveSessionSampleRate(authBoot: BootAuthResponse): number {
        return authBoot.featureFlags?.[FeatureFlags.STORE_RUM_FULL_SESSION_SAMPLING]
            ? FULL_SESSION_SAMPLE_RATE
            : DEFAULT_SESSION_SAMPLE_RATE;
    }

    private runOrQueue(operation: CoralogixOperation): void {
        if (this.isInitialized && this.sdk) {
            operation(this.sdk);
            return;
        }

        if (this.operationQueue.length === MAX_QUEUED_OPERATIONS) {
            this.operationQueue.shift();
        }
        this.operationQueue.push(operation);
    }

    private flushOperationQueue(): void {
        if (!this.sdk || !this.isInitialized) return;

        this.operationQueue.splice(0).forEach((operation) => {
            operation(this.sdk as CoralogixSdk);
        });
    }
}

export default new CoralogixRumService();

const enrichStoreJourneyEvent = (
    eventName: LoggerEventNames,
    eventData: Record<string, unknown>
): Record<string, unknown> => {
    if (!JOURNEY_EVENT_NAMES.has(eventName)) {
        return eventData;
    }
    return {
        ...eventData,
        ...getStoreJourneyContext(),
    };
};
